import { Circle } from '@/components/common/charts/shapes/shapes';
import Icon from '@/components/common/icons/Icon';
import { toDollars, toDollarsWithCents } from '@/util/currency';
import { Box, Center, FlexProps } from '@chakra-ui/react';
import { IconName } from '@common/react/icons';
import { ageOnDate, useCurrentUser } from '@common/react/user';
import { dt } from '@common/util/dateTime';
import { toPx } from '@common/util/styles';
import { isNumber } from '@common/util/types';
import {
    GoalValueNode,
    GoalValueSeries,
    isTickInRange,
    makeTick,
    nowTick,
    Tick,
    TickRange,
} from '@common/valueSeries';
import { Group } from '@visx/group';
import React, { memo, useCallback, useMemo } from 'react';
import {
    DEFAULT_MARGIN,
    getValueNodeForDate,
    ScaleNumeric,
    ScaleTickRange,
    TooltipCursorEventHandler,
    useTickRangeTooltipInfo,
    useXScaleForTickRange,
    useYScaleNumeric,
} from '../common/charts/chartHelper';
import {
    CHART_TOOLTIP_BASE_HEIGHT,
    ChartTooltip,
    ChartTooltipRow,
} from '../common/charts/ChartTooltip';
import CursorTracker from '../common/charts/CursorTracker';
import DatesXAxis from '../common/charts/DatesXAxis';
import { EntityLine } from '../common/charts/EntityLine';
import NumericYAxisWithGrid from '../common/charts/NumericYAxisWithGrid';
import Line from '../common/charts/shapes/Line';
import { TodayLine, useTodayX } from '../common/charts/TodayLine';
import VerticalGridLine from '../common/charts/VerticalGridline';
import { MotionBox } from '../common/Motion';
import { hoverStyleProps } from '../common/styleHelpers';
import { Text } from '../common/Typography';

export const goalsChartMargin = DEFAULT_MARGIN;

const TOOLTIP_WIDTH = 230;
const TOOLTIP_ROW_HEIGHT = 24;

export interface GoalsChartCallout {
    iconName: IconName;
    date: string;
    value: number;
    color: string;
    onClick?: () => void;
}

export interface GoalsChartEntityParams {
    id: string;
    valueSeries: GoalValueSeries;
    color: string;
    strokeWidth?: number;
    gradientAlphaMax?: number;
    callout?: GoalsChartCallout;
}

type Datum = GoalValueNode & {
    label: string;
    balance: number;
    color: string;
};

type TooltipData = Tick & {
    goals: Datum[];
};

export interface GoalsChartProps extends FlexProps {
    range: TickRange;
    entities: GoalsChartEntityParams[];
    width: number;
    height: number;
    tooltipHeaderRight?: string;
}

export default memo(
    function GoalsChart({
        range,
        entities,
        width,
        height,
        tooltipHeaderRight,
        ...props
    }: GoalsChartProps) {
        const { userInfo } = useCurrentUser();

        const innerWidth =
            width - goalsChartMargin.left - goalsChartMargin.right;
        const innerHeight =
            height - goalsChartMargin.top - goalsChartMargin.bottom;

        const todayTick = nowTick(range.unit);
        const todayTickDate = todayTick.date;

        const datumFromValueNode = useCallback(
            (vn: GoalValueNode): Datum | undefined => {
                if (!isNumber(vn.balance)) return;
                return {
                    ...vn,
                    label: vn.name,
                    color: vn.color,
                    balance: vn.balance,
                };
            },
            []
        );

        const [dataSeries, maxValueForDate, tooltipDataForDate, yDomain] =
            useMemo(() => {
                let min = Infinity;
                let max = 0;
                const series: {
                    entity: GoalsChartEntityParams;
                    recordedData: Datum[];
                    forecastData: Datum[];
                }[] = [];

                for (const entity of entities) {
                    const recordedData: Datum[] = [];
                    const forecastData: Datum[] = [];
                    for (const vn of entity.valueSeries.values) {
                        const d = datumFromValueNode(vn);
                        if (!d) continue;
                        if (d.date >= todayTickDate) forecastData.push(d);
                        if (d.date <= todayTickDate) recordedData.push(d);

                        min = Math.min(d.balance, min);
                        max = Math.max(d.balance, max);
                    }
                    if (
                        entity.callout &&
                        isTickInRange(
                            range,
                            makeTick(entity.callout.date, range.unit)
                        )
                    ) {
                        min = Math.min(entity.callout.value, min);
                        max = Math.max(entity.callout.value, max);
                    }
                    series.push({ entity, recordedData, forecastData });
                }

                const tooltipDataForDate = (date: string): TooltipData => {
                    const tick = makeTick(date, range.unit);
                    const data: TooltipData = { ...tick, goals: [] };
                    for (const e of entities) {
                        const vn = getValueNodeForDate(e.valueSeries, date);
                        if (vn) {
                            const datum = datumFromValueNode(vn);
                            if (datum) {
                                data.goals.push(datum);
                            }
                        }
                    }
                    return data;
                };

                const maxValueForDate = (date: string) => {
                    const tooltipData = tooltipDataForDate(date);
                    return Math.max(
                        ...tooltipData.goals.map((g) => g.balance),
                        0
                    );
                };

                // Ensure we always have a non-zero y max if the user has
                // no goals, yet.
                // TODO: Tear this out when we add a real placeholder view.
                max = max === 0 ? 100_000_00 : max;

                return [
                    series,
                    maxValueForDate,
                    tooltipDataForDate,
                    [min, max],
                ];
            }, [entities, range, datumFromValueNode, todayTickDate]);

        const xScale = useXScaleForTickRange({ width: innerWidth, range });
        const yScale = useYScaleNumeric({
            domain: yDomain,
            height: innerHeight,
            shouldClamp: false,
        });

        const {
            tooltip,
            handleCursor,
            handleCursorLeave,
            handleTooltipMouseEnter,
        } = useTickRangeTooltipInfo({
            xScale,
            yScale,
            maxValueForDate,
            tooltipDataForDate,
            innerWidth,
            margin: goalsChartMargin,
            tooltipWidth: TOOLTIP_WIDTH,
        });

        // memoized point getters for entity lines
        const xGetter = useCallback(
            (d: Datum) => xScale(d.date) ?? 0,
            [xScale]
        );
        const yGetter = useCallback(
            (d: Datum) => yScale(d.balance ?? 0),
            [yScale]
        );

        const tooltipTop = Math.max(
            tooltip.midpointY -
                CHART_TOOLTIP_BASE_HEIGHT -
                TOOLTIP_ROW_HEIGHT * (tooltip.data?.goals.length ?? 0),
            goalsChartMargin.top + 20
        );
        const todayX = useTodayX({ range, xScale });

        if (innerWidth < 0 || innerHeight < 0) return null;

        return (
            <Box {...props} w={width} h={height} position="relative">
                <svg width={width} height={height}>
                    <Group
                        left={goalsChartMargin.left}
                        top={goalsChartMargin.top}
                    >
                        <NumericYAxisWithGrid
                            scale={yScale}
                            width={innerWidth}
                            height={innerHeight}
                        />
                        <DatesXAxis
                            top={innerHeight}
                            scale={xScale}
                            unit={range.unit}
                        />
                        {dataSeries.map(
                            ({ entity, recordedData, forecastData }) => {
                                return (
                                    <React.Fragment
                                        key={'entity-line-' + entity.id}
                                    >
                                        {!!recordedData.length && (
                                            <EntityLine
                                                color={entity.color}
                                                data={recordedData}
                                                x={xGetter}
                                                y={yGetter}
                                                yScale={yScale}
                                                strokeWidth={entity.strokeWidth}
                                                gradientAlphaMax={
                                                    entity.gradientAlphaMax
                                                }
                                                solid
                                            />
                                        )}
                                        {!!forecastData.length && (
                                            <EntityLine
                                                color={entity.color}
                                                data={forecastData}
                                                x={xGetter}
                                                y={yGetter}
                                                yScale={yScale}
                                                strokeWidth={entity.strokeWidth}
                                                gradientAlphaMax={
                                                    entity.gradientAlphaMax
                                                }
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            }
                        )}
                        <TodayLine todayX={todayX} innerHeight={innerHeight} />
                        {tooltip.visible && (
                            // TODO: Update to intersect all lines with correct
                            // dot colors
                            <>
                                <VerticalGridLine
                                    x={tooltip.midpointX}
                                    height={innerHeight}
                                />
                                {tooltip.data?.goals
                                    .sort((a, b) => a.balance - b.balance)
                                    .map((g) => {
                                        const y = yScale(g.balance);

                                        return (
                                            <Circle
                                                key={`chart-intersect-${tooltip.midpointX}-${g.label}-${g.goalId}`}
                                                x={tooltip.midpointX}
                                                y={y}
                                                color={g.color}
                                            />
                                        );
                                    })}
                            </>
                        )}
                        {dataSeries.map(({ entity }) => {
                            if (!entity.callout) return null;
                            return (
                                entity.callout && (
                                    <CalloutLine
                                        key={'callout-line-' + entity.id}
                                        callout={entity.callout}
                                        xScale={xScale}
                                        yScale={yScale}
                                        range={range}
                                        height={innerHeight}
                                    />
                                )
                            );
                        })}

                        <CursorTracker
                            w={innerWidth}
                            h={innerHeight}
                            onCursor={handleCursor}
                            onCursorLeave={handleCursorLeave}
                        />
                    </Group>
                </svg>

                {/* Render callout bubble outside svg for full html support */}
                {dataSeries.map(({ entity }) => {
                    if (!entity.callout) return null;
                    return (
                        entity.callout && (
                            <CalloutBubble
                                handleCursor={handleCursor}
                                key={'callout-bubble-' + entity.id}
                                callout={entity.callout}
                                xScale={xScale}
                                yScale={yScale}
                                range={range}
                            />
                        )
                    );
                })}

                {/* Render tooltip outside the svg for full html support */}
                {tooltip.visible &&
                    tooltip.data &&
                    tooltip.data.goals.some((g) => isNumber(g.balance)) &&
                    userInfo.dob && (
                        <ChartTooltip
                            key={Math.random()}
                            top={tooltipTop}
                            left={tooltip.left}
                            title={dt(tooltip.data.date).format(
                                range.unit === 'day'
                                    ? 'ddd, MMM DD YYYY'
                                    : 'MMM, YYYY'
                            )}
                            headerRight={
                                <Text
                                    key="tooltip-header-right"
                                    color="gray"
                                    fontSize="sm"
                                >
                                    {tooltipHeaderRight
                                        ? tooltipHeaderRight
                                        : `Age ${ageOnDate(
                                              userInfo.dob,
                                              tooltip.data.date
                                          )}`}
                                </Text>
                            }
                            onMouseEnter={handleTooltipMouseEnter}
                            w={TOOLTIP_WIDTH}
                        >
                            {tooltip.data.goals.map((g) => {
                                return (
                                    <ChartTooltipRow
                                        key={
                                            'goal-tooltip-row-' +
                                            g.goalId +
                                            g.label
                                        }
                                        labelColor={g.color}
                                        label={g.label}
                                        value={
                                            // Show cents for past balances only
                                            g.date < todayTick.date
                                                ? toDollarsWithCents(g.balance)
                                                : toDollars(g.balance)
                                        }
                                        h={toPx(TOOLTIP_ROW_HEIGHT)}
                                    />
                                );
                            })}
                        </ChartTooltip>
                    )}
            </Box>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.range === nextProps.range &&
            prevProps.height === nextProps.height &&
            prevProps.width === nextProps.width &&
            prevProps.entities === nextProps.entities
        );
    }
);

interface CalloutProps {
    callout: GoalsChartCallout;
    range: TickRange;
    xScale: ScaleTickRange;
    yScale: ScaleNumeric;
}

interface CalloutPoint {
    x: number;
    y: number;
}

const isCalloutInRange = (
    callout: GoalsChartCallout,
    range: TickRange
): boolean => {
    return isTickInRange(range, makeTick(callout.date, range.unit));
};

const getCalloutPoint = ({
    range,
    callout,
    xScale,
    yScale,
}: CalloutProps): CalloutPoint | undefined => {
    if (!isCalloutInRange(callout, range)) return;
    const x = xScale(callout.date);
    const y = yScale(callout.value);
    if (!isNumber(x)) return;

    return { x, y };
};

function CalloutLine(props: CalloutProps & { height: number }) {
    const point = getCalloutPoint(props);
    if (!point) return null;
    const { x, y } = point;

    return (
        <Line
            key={'callout-line-' + props.callout.date}
            from={{ x, y }}
            to={{ x, y: props.height }}
            stroke={props.callout.color}
            strokeWidth={1}
            pointerEvents="none"
            isAnimated={true}
        />
    );
}

const CALLOUT_BUBBLE_SIZE = 20;

function CalloutBubble(
    props: CalloutProps & {
        handleCursor: TooltipCursorEventHandler;
    }
) {
    const point = getCalloutPoint(props);
    if (!point) return null;
    const { callout, handleCursor } = props;
    const x = point.x + goalsChartMargin.left;
    const y = point.y + goalsChartMargin.top;

    const onClick = callout.onClick ? () => callout?.onClick?.() : undefined;

    return (
        <MotionBox
            onMouseEnter={() => handleCursor({ x, y })}
            key={'callout-bubble-' + callout.date}
            position="absolute"
            initial={false}
            animate={{
                left: x - CALLOUT_BUBBLE_SIZE / 2,
                top: y - CALLOUT_BUBBLE_SIZE / 2,
            }}
            w={toPx(CALLOUT_BUBBLE_SIZE)}
            h={toPx(CALLOUT_BUBBLE_SIZE)}
            rounded="full"
            bg={callout.color}
            {...(callout.onClick ? hoverStyleProps({ shadow: 'sm' }) : {})}
            onClick={onClick}
        >
            <Center w="full" h="full">
                <Icon name={callout.iconName} size="sm" color="white" />
            </Center>
        </MotionBox>
    );
}
